import type { StaticCollider } from '../sim/sim';
import type { SurfaceId } from '../sim/types';
import { SURFACE_IDS } from '../sim/surfaces';
import type { BuiltStage, InstanceSet, MeshData, PropInstance, RoadStageSpec, Track } from './types';
import { buildTrack, TrackIndex } from './track';
import { TerrainModel, CORRIDOR, smoothstep } from './terrain';
import { computeNormals } from './pad';
import { hash01, Noise2D } from './noise';
import { inClearing, layoutCrowd, type CrowdSite } from './crowd';
import { RoadDamage } from './damage';

export interface RoadBuildOptions {
  /** Build render meshes (terrain chunks, ribbon) — tests only need colliders and surfaces. */
  meshes?: boolean;
  /** Vegetation density multiplier (graphics preset). */
  vegetation?: number;
}

const RIBBON_CHUNK = 100;

/** Lateral columns of the road cross-section (offsets from the centre, + = left). */
function ribbonColumns(hw: number): number[] {
  return [hw + 3.2, hw + 2.1, hw + 1.0, hw + 0.4, hw, hw - 0.5, 1.17, 0.82, 0.47, 0, -0.47, -0.82, -1.17, -hw + 0.5, -hw, -hw - 0.4, -hw - 1.0, -hw - 2.1, -hw - 3.2];
}
const BANK = [5, 8, 12, 17, 24];

/**
 * The rendered ribbon reaches past the ditch onto plain ground (hw + RIBBON_EXT), where it follows
 * the terrain, so the seam with the coarse terrain mesh falls where both are the same ground cover
 * (no sawtooth along the verge). Narrower on the inside of tight bends so it can't fold over itself.
 */
const RIBBON_EXT = 7;
function ribbonExt(track: Track, i: number): { left: number; right: number } {
  const k = track.curvature[i];
  const hw = track.width[i] / 2;
  const inner = Math.abs(k) > 1e-6 ? Math.max(3.2, Math.min(RIBBON_EXT, 0.75 / Math.abs(k) - hw)) : RIBBON_EXT;
  return k > 0 ? { left: inner, right: RIBBON_EXT } : { left: RIBBON_EXT, right: inner };
}
/** Render ribbon columns: the physics cross-section plus the outer band on each side. */
function visualColumns(hw: number, left: number, right: number): number[] {
  const band = (e: number) => [3.2 + (e - 3.2) * 0.32, 3.2 + (e - 3.2) * 0.68, e];
  const base = ribbonColumns(hw);
  const l = band(left).map((o) => hw + o);
  const r = band(right).map((o) => -hw - o);
  return [l[2], l[1], l[0], ...base, r[0], r[1], r[2]];
}
const RIBBON_COLS = 25;
/** How deep the terrain is carved under the ribbon, and the band (offsets beyond hw) where it comes back up. */
function carveAt(ext: number): { depth: number; from: number; to: number } {
  const t = Math.max(0, Math.min(1, (ext - 3.2) / (RIBBON_EXT - 3.2)));
  return { depth: 0.35 + 0.55 * t, from: Math.max(2.6, ext - 3.4), to: Math.max(3.6, ext - 1.8) };
}

export function buildRoad(spec: RoadStageSpec, opts: RoadBuildOptions = {}): BuiltStage {
  const meshes = opts.meshes ?? true;
  const track = buildTrack(spec, 1);
  const index = new TrackIndex(track);
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < track.s.length; i++) {
    minX = Math.min(minX, track.x[i]);
    maxX = Math.max(maxX, track.x[i]);
    minZ = Math.min(minZ, track.z[i]);
    maxZ = Math.max(maxZ, track.z[i]);
  }
  const margin = 900;
  const bounds = { minX: minX - margin, maxX: maxX + margin, minZ: minZ - margin, maxZ: maxZ + margin };
  const damage = spec.roadFeatures?.length ? new RoadDamage(track, spec.roadFeatures, spec.seed) : null;
  const terrain = new TerrainModel(spec, track, bounds, damage);
  const zoneOf = (i: number): 'forest' | 'open' | 'hamlet' => {
    const s = track.s[i];
    for (const z of spec.zones) if (s >= z.from && s < z.to) return z.type;
    return 'forest';
  };

  const colliders: StaticCollider[] = [];
  // --- Physics: road-aligned corridor trimesh (road + shoulders + ditches + banks).
  const n = track.s.length;
  const CH = 300;
  for (let c0 = 0; c0 < n - 1; c0 += CH) {
    const c1 = Math.min(n - 1, c0 + CH);
    const verts: number[] = [];
    let cols = 0;
    for (let i = c0; i <= c1; i++) {
      const hw = track.width[i] / 2;
      const offs = [hw + BANK[4], hw + BANK[3], hw + BANK[2], hw + BANK[1], hw + BANK[0], ...ribbonColumns(hw), -hw - BANK[0], -hw - BANK[1], -hw - BANK[2], -hw - BANK[3], -hw - BANK[4]];
      cols = offs.length;
      for (const d of offs) {
        const x = track.x[i] + track.tz[i] * d;
        const z = track.z[i] - track.tx[i] * d;
        const ad = Math.abs(d);
        let y: number;
        if (ad <= hw + 3.21 && !(terrain.bridge[i] && ad > hw)) y = terrain.roadProfile(i, d, x, z);
        else y = terrain.heightAt(x, z);
        verts.push(x, y, z);
      }
    }
    const rows = c1 - c0 + 1;
    const idx: number[] = [];
    for (let r = 0; r < rows - 1; r++) {
      for (let k = 0; k < cols - 1; k++) {
        const a = r * cols + k;
        const b = a + 1;
        const c = a + cols;
        const d = c + 1;
        idx.push(a, c, b, b, c, d);
      }
    }
    colliders.push({ kind: 'trimesh', vertices: new Float32Array(verts), indices: new Uint32Array(idx), friction: 0.8 });
  }

  // --- Physics: coarse heightfield for everything else (lowered under the corridor trimesh).
  const hfCell = 8;
  const hfMargin = 260;
  const hx0 = minX - hfMargin;
  const hz0 = minZ - hfMargin;
  const ncols = Math.ceil((maxX - minX + 2 * hfMargin) / hfCell);
  const nrows = Math.ceil((maxZ - minZ + 2 * hfMargin) / hfCell);
  // Rapier heightfield: (nrows+1) × (ncols+1) heights, column-major; rows run along Z, columns along X.
  const heights = new Float32Array((nrows + 1) * (ncols + 1));
  for (let c = 0; c <= ncols; c++) {
    for (let r = 0; r <= nrows; r++) {
      const x = hx0 + c * hfCell;
      const z = hz0 + r * hfCell;
      let h = terrain.heightAt(x, z);
      const lat = terrain.lateral(x, z);
      if (lat.i >= 0 && Math.abs(lat.d) < track.width[lat.i] / 2 + BANK[4] - 1) h -= 2.5;
      heights[c * (nrows + 1) + r] = h;
    }
  }
  colliders.push({
    kind: 'heightfield',
    nrows,
    ncols,
    heights,
    scale: [ncols * hfCell, 1, nrows * hfCell],
    center: [hx0 + (ncols * hfCell) / 2, 0, hz0 + (nrows * hfCell) / 2],
    friction: 0.8,
  });

  // --- Surfaces.
  const surfaceAt = (x: number, z: number): SurfaceId => {
    const { i, d } = terrain.lateral(x, z);
    if (i < 0) return 'grass';
    const hw = track.width[i] / 2;
    const ad = Math.abs(d);
    const c = SURFACE_IDS[track.surface[i]];
    if (c === 'water' && ad < hw + 4) return 'water';
    if (ad <= hw) {
      if (damage && damage.waterAt(i, d, damage.along(i, x, z))) return 'water';
      if (terrain.bridge[i]) return 'concrete';
      if (c === 'gravel' && ad > hw - 0.9) return 'loose';
      return c;
    }
    if (ad <= hw + 1.0) return c === 'tarmac' ? 'gravel' : 'loose';
    if (ad <= hw + 3.2) return c === 'mud' || c === 'wet' ? 'mud' : 'grass';
    return 'grass';
  };

  // --- Ground cover shared by the terrain and the road verges (no seam where they meet):
  // forest floor under the trees, rock on steep faces, grass in the open, dirt along the road.
  const noise = new Noise2D(spec.seed + 99);
  const forestness = (x: number, z: number, zone: 'forest' | 'open' | 'hamlet', ad: number): number => {
    const clump = noise.fbm(x / 90, z / 90, 3);
    // Grass verge for the first few metres, then the forest floor under the trees.
    if (zone === 'forest') return smoothstep(4, 9, ad) * (ad < 60 ? 1 : Math.min(1, 0.65 * Math.exp(-(ad - 60) / 200) + Math.max(0, clump) * 0.8));
    if (zone === 'hamlet') return ad < 28 ? 0 : 0.15 + 0.3 * Math.max(0, clump);
    return Math.max(0, clump - 0.12) * 1.4 * smoothstep(12, 40, ad);
  };
  const splatAt = (x: number, z: number, ny: number, out: number[], terrainVertex = false): void => {
    let si = terrain.base.nearestSample(x, z);
    if (si < 0) si = 0;
    const lat = terrain.lateral(x, z);
    const hw = lat.i >= 0 ? track.width[lat.i] / 2 : 3.1;
    // Terrain vertices under the ribbon are hidden, but their weights would be interpolated into
    // the visible triangles beyond its edge (a sawtooth along the verge): give them the edge's.
    const ad0 = lat.i >= 0 ? Math.abs(lat.d) : terrain.base.distanceAt(x, z);
    let ad = ad0;
    if (terrainVertex && lat.i >= 0) {
      const e = ribbonExt(track, lat.i);
      ad = Math.max(ad0, hw + (lat.d > 0 ? e.left : e.right));
    }
    const rock = smoothstep(0.18, 0.4, 1 - ny);
    // Dry earth along the road, fading out within the ribbon (the terrain mesh never carries it).
    const dirt = (1 - rock) * (1 - smoothstep(hw + 1.0, hw + 3.2, ad)) * 0.5;
    const f = Math.min(1, forestness(x, z, zoneOf(si), ad));
    const rest = Math.max(0, 1 - rock - dirt);
    out[0] = rest * f;
    out[1] = rock;
    out[2] = rest * (1 - f);
  };

  // --- Timber stacks at the roadside in the forest (a strong forest-stage cue); trees are kept
  // clear of them.
  const logPiles: { x: number; z: number; y: number; yaw: number }[] = [];
  for (let s0 = spec.startDistance + 180; s0 < spec.finishDistance - 100; s0 += 430 + 260 * hash01(Math.round(s0), 11, spec.seed)) {
    const i = Math.round(s0);
    if (zoneOf(i) !== 'forest' || Math.abs(track.curvature[i]) > 1 / 120 || terrain.bridge[i]) continue;
    const side = hash01(i, 12, spec.seed) < 0.5 ? 1 : -1;
    const off = side * (track.width[i] / 2 + 6.5);
    const x = track.x[i] + track.tz[i] * off;
    const z = track.z[i] - track.tx[i] * off;
    logPiles.push({ x, z, y: terrain.heightAt(x, z), yaw: Math.atan2(track.tx[i], track.tz[i]) });
  }

  // --- Spectator hotspots (P1-6): sites from stage.json, cleared of trees like the log piles.
  const crowdSites: CrowdSite[] = [];
  for (const p of spec.props) {
    if (p.type !== 'spectators') continue;
    const i = Math.max(0, Math.min(n - 1, Math.round(p.s)));
    const off = p.offset ?? 14;
    const side = Math.sign(off) || 1;
    const along: [number, number] = [track.tx[i], track.tz[i]];
    const away: [number, number] = [track.tz[i] * side, -track.tx[i] * side];
    crowdSites.push({ x: track.x[i] + track.tz[i] * off, z: track.z[i] - track.tx[i] * off, along, away, width: 12 });
  }
  const roadClearance = (x: number, z: number) => {
    const { i, d } = terrain.lateral(x, z);
    return i < 0 ? Infinity : Math.abs(d) - track.width[i] / 2;
  };

  // --- Vegetation and rocks (deterministic jittered grid; fine cells with a low fill probability
  // read as random scatter, not rows).
  const veg = opts.vegetation ?? 1;
  const trees: number[][] = [[], [], []]; // spruce, pine, young spruce
  const rocks: number[] = [];
  const bushes: number[] = [];
  const cell = 3.5;
  const fill = (cell * cell) / (5.5 * 5.5);
  const vx0 = minX - 450;
  const vz0 = minZ - 450;
  const gx = Math.ceil((maxX - minX + 900) / cell);
  const gz = Math.ceil((maxZ - minZ + 900) / cell);
  for (let a = 0; a < gx; a++) {
    for (let b = 0; b < gz; b++) {
      const r1 = hash01(a, b, spec.seed);
      const r2 = hash01(a, b, spec.seed + 1);
      const r3 = hash01(a, b, spec.seed + 2);
      const x = vx0 + (a + r1) * cell;
      const z = vz0 + (b + r2) * cell;
      const dist = terrain.base.distanceAt(x, z);
      if (dist > 320) continue;
      let si = terrain.base.nearestSample(x, z);
      if (si < 0) si = 0;
      const zone = zoneOf(si);
      let ad = dist;
      let hw = 3.1;
      const lat = terrain.lateral(x, z);
      if (lat.i >= 0) {
        ad = Math.abs(lat.d);
        hw = track.width[lat.i] / 2;
        if (terrain.bridge[lat.i] && ad < 40) continue;
      }
      if (ad < hw + 3.6) continue;
      if (logPiles.some((l) => Math.abs(l.x - x) < 6 && Math.abs(l.z - z) < 6)) continue;
      if (crowdSites.some((c) => inClearing(c, x, z))) continue;
      // Streams: no trees in the channels.
      let wet = false;
      for (const st of terrain.streams) if (Math.abs((x - st.x) * st.dx + (z - st.z) * st.dz) < st.width * 0.9) wet = true;
      if (wet) continue;
      const clump = noise.fbm(x / 90, z / 90, 3);
      let p: number;
      if (zone === 'forest') p = ad < 60 ? 0.95 : 0.6 * Math.exp(-(ad - 60) / 200);
      else if (zone === 'hamlet') p = ad < 28 ? 0 : 0.12 + 0.2 * Math.max(0, clump);
      else p = Math.max(0, clump - 0.12) * 0.9 * smoothstep(12, 40, ad);
      p *= spec.terrain.treeDensity * veg * (0.75 + 0.5 * (clump + 0.5)) * fill;
      const y = terrain.heightAt(x, z);
      if (r3 < p) {
        // Young spruce at the forest edge and in clearings; spruce/pine mix elsewhere.
        const edge = ad < hw + 12 || noise.noise(x / 140 + 5, z / 140) > 0.32;
        const kind = edge && r1 < 0.5 ? 2 : r1 < 0.68 ? 0 : 1;
        const sc = kind === 2 ? 0.35 + 0.3 * hash01(a, b, spec.seed + 3) : 0.72 + 0.55 * hash01(a, b, spec.seed + 3);
        trees[kind].push(x, y, z, r2 * Math.PI * 2, sc);
        if (ad < hw + 26) colliders.push({ kind: 'cylinder', pos: [x, y + 3, z], halfHeight: 3.5, radius: 0.16 + 0.12 * sc, mat: 'wood' });
      } else if (r3 > 1 - 0.015 * fill * 1.5 - (ad < hw + 9 ? 0.02 * fill : 0) && ad > hw + 3.4) {
        const sc = 0.35 + 0.9 * hash01(a, b, spec.seed + 4) * (zone === 'open' ? 1.6 : 1);
        rocks.push(x, y - sc * 0.3, z, r1 * 6.28, sc);
        if (ad < hw + 22 && sc > 0.45) colliders.push({ kind: 'ball', pos: [x, y + sc * 0.2, z], radius: sc * 0.85, mat: 'rock' });
      } else if (r3 > 1 - 0.085 * fill * 1.4 && zone !== 'open' && ad < 70) {
        bushes.push(x, y, z, r2 * 6.28, 0.6 + 0.7 * hash01(a, b, spec.seed + 5));
      }
    }
  }

  // --- Stage furniture and hazards from stage.json.
  const props: PropInstance[] = [];
  let spectatorIndex = 0;
  const at = (s: number, off = 0) => {
    const i = Math.max(0, Math.min(n - 1, Math.round(s)));
    const x = track.x[i] + track.tz[i] * off;
    const z = track.z[i] - track.tx[i] * off;
    return { i, x, z, y: terrain.heightAt(x, z), yaw: Math.atan2(track.tx[i], track.tz[i]) };
  };
  for (const p of spec.props) {
    const hwAt = track.width[Math.max(0, Math.min(n - 1, Math.round(p.s)))] / 2;
    switch (p.type) {
      case 'arch': {
        const c = at(p.s, 0);
        props.push({ type: 'arch', x: c.x, y: track.y[c.i], z: c.z, yaw: c.yaw, scale: 1, text: p.text, width: hwAt * 2 + 3.4 });
        for (const side of [1, -1]) {
          const q = at(p.s, side * (hwAt + 1.7));
          colliders.push({ kind: 'cylinder', pos: [q.x, q.y + 2.5, q.z], halfHeight: 2.5, radius: 0.25, mat: 'metal' });
        }
        break;
      }
      case 'board': {
        const q = at(p.s, p.offset ?? -(hwAt + 2.4));
        props.push({ type: 'board', x: q.x, y: q.y, z: q.z, yaw: q.yaw, scale: 1, text: p.text });
        colliders.push({ kind: 'cuboid', pos: [q.x, q.y + 1.2, q.z], yaw: q.yaw, half: [0.6, 1.2, 0.08], mat: 'metal' });
        break;
      }
      case 'tape': {
        const a = at(p.s, 0);
        const len = (p.to ?? p.s + 8) - p.s;
        for (const side of [1, -1]) {
          const q = at(p.s + len / 2, side * (hwAt + 3.6));
          props.push({ type: 'tape', x: q.x, y: q.y, z: q.z, yaw: a.yaw, scale: 1, length: len + 10 });
        }
        break;
      }
      case 'bale': {
        // Bales on the outside of the junction corner.
        const i = Math.round(p.s);
        const side = track.curvature[Math.max(0, Math.min(n - 1, i))] > 0 ? -1 : 1;
        for (let k = -2; k <= 2; k++) {
          const q = at(p.s + k * 1.4, side * (hwAt + 2.6));
          props.push({ type: 'bale', x: q.x, y: q.y, z: q.z, yaw: q.yaw, scale: 1 });
          colliders.push({ kind: 'cuboid', pos: [q.x, q.y + 0.45, q.z], yaw: q.yaw, half: [0.6, 0.45, 0.62], mat: 'straw' });
        }
        break;
      }
      case 'fence': {
        // Post-and-rail fence following the road in straight 6 m panels; panels that would come
        // within 2.5 m of any road (junctions, hairpins) or into a crowd's clearing are left out.
        const to = p.to ?? p.s + 60;
        const off = p.offset ?? hwAt + 4;
        for (let s = p.s; s < to - 1; s += 6) {
          const a = at(s, off);
          const b = at(Math.min(to, s + 6), off);
          if (roadClearance(a.x, a.z) < 2.5 || roadClearance(b.x, b.z) < 2.5) continue;
          if (crowdSites.some((c) => inClearing(c, a.x, a.z) || inClearing(c, b.x, b.z))) continue;
          const len = Math.hypot(b.x - a.x, b.z - a.z);
          if (len < 1) continue;
          props.push({ type: 'fence', x: (a.x + b.x) / 2, y: Math.min(a.y, b.y), z: (a.z + b.z) / 2, yaw: Math.atan2(b.x - a.x, b.z - a.z), scale: 1, length: len });
        }
        break;
      }
      case 'barrier': {
        const to = p.to ?? p.s + 20;
        for (let s = p.s; s < to; s += 4) {
          const q = at(s + 2, p.offset ?? hwAt + 1.6);
          props.push({ type: 'barrier', x: q.x, y: q.y, z: q.z, yaw: q.yaw, scale: 1, length: 4 });
          colliders.push({ kind: 'cuboid', pos: [q.x, q.y + 0.45, q.z], yaw: q.yaw, half: [0.25, 0.45, 2.0], mat: 'concrete' });
        }
        break;
      }
      case 'bridge': {
        const from = p.s;
        const to = p.to ?? p.s + 30;
        const mid = at((from + to) / 2, 0);
        props.push({ type: 'bridge', x: mid.x, y: track.y[mid.i], z: mid.z, yaw: mid.yaw, scale: 1, length: to - from, width: hwAt * 2 });
        for (let s = from; s < to; s += 2) {
          for (const side of [1, -1]) {
            const q = at(s + 1, side * (hwAt + 0.15));
            colliders.push({ kind: 'cuboid', pos: [q.x, track.y[q.i] + 0.5, q.z], yaw: q.yaw, half: [0.12, 0.5, 1.05], mat: 'timber' });
          }
        }
        break;
      }
      case 'post': {
        if (p.text === 'house') {
          const q = at(p.s, Math.sign(p.offset ?? 1) * (hwAt + 9.5));
          const w = 6 + 3 * hash01(Math.round(p.s), 3, spec.seed);
          const dpt = 7 + 3 * hash01(Math.round(p.s), 4, spec.seed);
          props.push({ type: 'house', x: q.x, y: q.y, z: q.z, yaw: q.yaw, scale: 1, width: w, length: dpt });
          colliders.push({ kind: 'cuboid', pos: [q.x, q.y + 3, q.z], yaw: q.yaw, half: [w / 2, 3, dpt / 2], mat: 'brick' });
        } else {
          const q = at(p.s, p.offset ?? hwAt + 2);
          props.push({ type: 'post', x: q.x, y: q.y, z: q.z, yaw: q.yaw, scale: 1 });
        }
        break;
      }
      case 'rock': {
        const q = at(p.s, p.offset ?? hwAt + 1.5);
        const sc = p.scale ?? 1;
        rocks.push(q.x, q.y - sc * 0.25, q.z, p.s, sc);
        colliders.push({ kind: 'ball', pos: [q.x, q.y + sc * 0.2, q.z], radius: sc * 0.85, mat: 'rock' });
        break;
      }
      case 'log': {
        const q = at(p.s, p.offset ?? -(hwAt + 1.5));
        props.push({ type: 'log', x: q.x, y: q.y, z: q.z, yaw: q.yaw, scale: 1, length: 5 });
        colliders.push({ kind: 'cuboid', pos: [q.x, q.y + 0.25, q.z], yaw: q.yaw, half: [0.25, 0.25, 2.5], mat: 'timber' });
        break;
      }
      case 'spectators': {
        const q = at(p.s, p.offset ?? 14);
        const site = crowdSites[spectatorIndex++];
        const people = layoutCrowd(site, spec.seed + 101 * spectatorIndex, { heightAt: (x, z) => terrain.heightAt(x, z), roadClearance });
        props.push({ type: 'spectators', x: q.x, y: q.y, z: q.z, yaw: q.yaw, scale: 1, width: site.width, people });
        // Marshal tape between the crowd and the road.
        // Shortened where the road folds back (junctions, hairpins) so both ends stay 3 m off it.
        const t = at(p.s, (p.offset ?? 14) - Math.sign(p.offset ?? 14) * 2.2);
        const end = (u: number) => roadClearance(t.x + Math.sin(t.yaw) * u, t.z + Math.cos(t.yaw) * u);
        let len = site.width + 3;
        while (len > 4 && (end(-len / 2) < 3 || end(len / 2) < 3)) len -= 1;
        props.push({ type: 'tape', x: t.x, y: t.y, z: t.z, yaw: t.yaw, scale: 1, length: len });
        break;
      }
      default:
        break;
    }
  }

  for (const l of logPiles) {
    props.push({ type: 'logpile', x: l.x, y: l.y, z: l.z, yaw: l.yaw, scale: 1, length: 6 });
    colliders.push({ kind: 'cuboid', pos: [l.x, l.y + 0.6, l.z], yaw: l.yaw, half: [1.1, 0.6, 3], mat: 'timber' });
  }

  // --- Render meshes.
  const road: MeshData[] = [];
  const terrainMeshes: MeshData[] = [];
  const water: MeshData[] = [];
  const decals: MeshData[] = [];
  if (meshes) {
    for (let c0 = 0; c0 < n - 1; c0 += RIBBON_CHUNK) road.push(ribbonMesh(track, terrain, c0, Math.min(n - 1, c0 + RIBBON_CHUNK), splatAt));
    terrainMeshes.push(...terrainChunks(terrain, track, bounds, splatAt));
    for (const st of terrain.streams) water.push(waterMesh(st));
    if (damage) {
      const pm = puddleMesh(track, terrain, damage);
      if (pm) water.push(pm);
      decals.push(...damageDecals(track, terrain, damage));
    }
  }

  const startI = Math.round(spec.startDistance);
  const spawnY = terrain.heightAt(track.x[startI], track.z[startI]);
  return {
    spec,
    colliders,
    surfaces: { surfaceAt },
    heightAt: (x, z) => terrain.heightAt(x, z),
    track,
    spawn: { x: track.x[startI], y: spawnY + 0.05, z: track.z[startI], heading: Math.atan2(track.tx[startI], track.tz[startI]) },
    terrain: terrainMeshes,
    road,
    instances: [
      { kind: 'spruce', data: new Float32Array(trees[0]) },
      { kind: 'pine', data: new Float32Array(trees[1]) },
      { kind: 'birch', data: new Float32Array(trees[2]) },
      { kind: 'rock', data: new Float32Array(rocks) },
      { kind: 'bush', data: new Float32Array(bushes) },
    ] as InstanceSet[],
    bounds,
    index,
    terrainModel: terrain,
    water,
    decals,
    props,
    chunkLength: RIBBON_CHUNK,
    groundAt: (x, z, out) => splatAt(x, z, 1, out),
    visualHeightAt: (x, z) => {
      const { i, d } = terrain.lateral(x, z);
      if (i >= 0 && Math.abs(d) <= track.width[i] / 2 + 3.2 && !terrain.bridge[i]) return terrain.roadProfile(i, d, x, z);
      return terrain.heightAt(x, z);
    },
  };
}

/** Road ribbon for samples [a, b] with per-vertex splat colour (r = road, g = loose, b = wet/mud). */
type SplatFn = (x: number, z: number, ny: number, out: number[], terrainVertex?: boolean) => void;

function ribbonMesh(track: Track, terrain: TerrainModel, a: number, b: number, splatAt: SplatFn): MeshData {
  const rows = b - a + 1;
  const cols = RIBBON_COLS;
  const pos = new Float32Array(rows * cols * 3);
  const uv = new Float32Array(rows * cols * 2);
  const col = new Float32Array(rows * cols * 3);
  const spl = new Float32Array(rows * cols * 3);
  const w = [0, 0, 0];
  for (let r = 0; r < rows; r++) {
    const i = a + r;
    const hw = track.width[i] / 2;
    const ext = ribbonExt(track, i);
    const offs = visualColumns(hw, ext.left, ext.right);
    const sid = SURFACE_IDS[track.surface[i]];
    const tarmac = sid === 'tarmac' || sid === 'concrete';
    for (let k = 0; k < cols; k++) {
      let d = offs[k];
      if (terrain.bridge[i]) d = Math.max(-hw, Math.min(hw, d));
      const x = track.x[i] + track.tz[i] * d;
      const z = track.z[i] - track.tx[i] * d;
      const ad = Math.abs(d);
      // The road cross-section out to the end of the ditch, then the terrain itself.
      const y = ad <= hw + 3.2 || terrain.bridge[i] ? terrain.roadProfile(i, d, x, z) : terrain.heightAt(x, z);
      const o = (r * cols + k) * 3;
      pos[o] = x;
      pos[o + 1] = y + 0.004;
      pos[o + 2] = z;
      uv[(r * cols + k) * 2] = d / 3;
      uv[(r * cols + k) * 2 + 1] = track.s[i] / 3;
      const onRoad = ad <= hw + 0.05 ? 1 : ad <= hw + 1.0 ? 0.45 : 0;
      const loose = tarmac ? (ad > hw ? 0.8 : 0) : ad > hw - 0.9 ? 0.85 : Math.abs(ad - 0) < 0.3 ? 0.35 : 0.12;
      // Wet road surfaces stay on the road and shoulder (the water's edge at a ford); beyond, only the
      // damp ditch bottom — a wet (glossy) verge mirrored the sky as a pale sheen.
      const ditchWet = ad > hw + 1.6 && ad < hw + 2.6 ? 0.35 : 0;
      const surfWet = sid === 'mud' ? 1 : sid === 'wet' ? 0.6 : sid === 'water' ? 0.9 : 0;
      const wetReach = sid === 'water' ? hw + 4 : hw + 1.0;
      const holeWet = terrain.damage && ad <= hw + 1 ? terrain.damage.wetness(i, d, track.s[i]) * 0.7 : 0;
      const wet = Math.max(ditchWet, holeWet, ad <= wetReach ? surfWet : ad <= wetReach + 1.2 ? surfWet * (1 - (ad - wetReach) / 1.2) : 0);
      col[o] = tarmac && ad <= hw ? 2 : onRoad; // 2 marks tarmac for the shader
      col[o + 1] = loose;
      col[o + 2] = wet;
      splatAt(x, z, 1, w);
      spl[o] = w[0];
      spl[o + 1] = w[1];
      spl[o + 2] = w[2];
    }
  }
  const idx: number[] = [];
  for (let r = 0; r < rows - 1; r++) {
    for (let k = 0; k < cols - 1; k++) {
      const p = r * cols + k;
      idx.push(p, p + 1, p + cols, p + 1, p + cols + 1, p + cols);
    }
  }
  const indices = new Uint32Array(idx);
  const normals = new Float32Array(pos.length);
  computeNormals(pos, indices, normals);
  // Tuck the outer edges under the terrain after the normals, so the edge shades like the ground.
  for (let r = 0; r < rows; r++) {
    pos[(r * cols) * 3 + 1] -= 0.12;
    pos[(r * cols + cols - 1) * 3 + 1] -= 0.12;
  }
  return { positions: pos, normals, uvs: uv, indices, colors: col, splat: spl };
}

/** Terrain chunks: 2.5 m grid near the road, coarser further out. */
function terrainChunks(terrain: TerrainModel, track: Track, bounds: { minX: number; maxX: number; minZ: number; maxZ: number }, splatAt: SplatFn): MeshData[] {
  const out: MeshData[] = [];
  const size = 160;
  for (let cx = bounds.minX; cx < bounds.maxX; cx += size) {
    for (let cz = bounds.minZ; cz < bounds.maxZ; cz += size) {
      const d = terrain.base.distanceAt(cx + size / 2, cz + size / 2);
      const res = d < 200 ? 3.2 : d < 500 ? 8 : 20;
      out.push(terrainChunk(terrain, track, cx, cz, size, res, splatAt));
    }
  }
  return out;
}

function terrainChunk(terrain: TerrainModel, track: Track, x0: number, z0: number, size: number, res: number, splatAt: SplatFn): MeshData {
  const w = [0, 0, 0];
  const n = Math.round(size / res);
  const v = n + 1;
  const pos = new Float32Array(v * v * 3);
  const uv = new Float32Array(v * v * 2);
  const col = new Float32Array(v * v * 3);
  const hgt = new Float32Array((v + 2) * (v + 2));
  const hgtN = new Float32Array((v + 2) * (v + 2));
  // Heights with a 1-cell apron for normals (taken from the uncarved surface).
  for (let j = -1; j <= n + 1; j++) {
    for (let i = -1; i <= n + 1; i++) {
      const x = x0 + i * res;
      const z = z0 + j * res;
      let h = terrain.heightAt(x, z);
      hgtN[(j + 1) * (v + 2) + (i + 1)] = h;
      const lat = terrain.lateral(x, z);
      if (lat.i >= 0) {
        const hw = track.width[lat.i] / 2;
        const ad = Math.abs(lat.d);
        const e = ribbonExt(track, lat.i);
        const c = carveAt(lat.d > 0 ? e.left : e.right);
        h -= c.depth * (1 - smoothstep(hw + c.from, hw + c.to, ad));
      }
      hgt[(j + 1) * (v + 2) + (i + 1)] = h;
    }
  }
  const normals = new Float32Array(v * v * 3);
  for (let j = 0; j <= n; j++) {
    for (let i = 0; i <= n; i++) {
      const k = j * v + i;
      const x = x0 + i * res;
      const z = z0 + j * res;
      const h = hgt[(j + 1) * (v + 2) + (i + 1)];
      pos[k * 3] = x;
      pos[k * 3 + 1] = h;
      pos[k * 3 + 2] = z;
      uv[k * 2] = x / 6;
      uv[k * 2 + 1] = z / 6;
      const hl = hgtN[(j + 1) * (v + 2) + i];
      const hr = hgtN[(j + 1) * (v + 2) + (i + 2)];
      const hd = hgtN[j * (v + 2) + (i + 1)];
      const hu = hgtN[(j + 2) * (v + 2) + (i + 1)];
      let nx = hl - hr;
      let ny = 2 * res;
      let nz = hd - hu;
      const l = Math.hypot(nx, ny, nz);
      nx /= l;
      ny /= l;
      nz /= l;
      normals[k * 3] = nx;
      normals[k * 3 + 1] = ny;
      normals[k * 3 + 2] = nz;
      // Splat weights: r = forest floor, g = rock (steep), b = grass; dirt = remainder.
      splatAt(x, z, ny, w, true);
      col[k * 3] = w[0];
      col[k * 3 + 1] = w[1];
      col[k * 3 + 2] = w[2];
    }
  }
  const idx = new Uint32Array(n * n * 6);
  let p = 0;
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const a = j * v + i;
      const b = a + 1;
      const c = a + v;
      const d = c + 1;
      idx[p++] = a;
      idx[p++] = c;
      idx[p++] = b;
      idx[p++] = b;
      idx[p++] = c;
      idx[p++] = d;
    }
  }
  return { positions: pos, normals, uvs: uv, indices: idx, colors: col };
}

function waterMesh(st: { x: number; z: number; dx: number; dz: number; width: number; waterY: number }): MeshData {
  // A long strip along the stream (perpendicular to the road), width ≈ channel width.
  const len = 300;
  const w = st.width * 0.9;
  const ax = st.dz;
  const az = -st.dx; // stream direction (across the road)
  const segs = 30;
  const pos: number[] = [];
  const uv: number[] = [];
  for (let i = 0; i <= segs; i++) {
    const t = (i / segs - 0.5) * len;
    for (const side of [-1, 1]) {
      pos.push(st.x + ax * t + st.dx * side * w * 0.5, st.waterY, st.z + az * t + st.dz * side * w * 0.5);
      uv.push(side * 0.5 + 0.5, t / 10);
    }
  }
  const idx: number[] = [];
  for (let i = 0; i < segs; i++) {
    const a = i * 2;
    // Counter-clockwise seen from above (the material is front-side only).
    idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const positions = new Float32Array(pos);
  const indices = new Uint32Array(idx);
  const normals = new Float32Array(positions.length);
  for (let i = 1; i < normals.length; i += 3) normals[i] = 1;
  return { positions, normals, uvs: new Float32Array(uv), indices };
}

/**
 * Pothole and washout decals: discs and gully strips just above the undamaged road surface (always
 * above the coarse ribbon), shaded in the shader as a damp, dark bowl. uv.x = radial (or across-
 * gully) position 0…1.3, uv.y = 1 for potholes that hold water.
 */
function damageDecals(track: Track, terrain: TerrainModel, damage: RoadDamage): MeshData[] {
  const pos: number[] = [];
  const uv: number[] = [];
  const nor: number[] = [];
  const idx: number[] = [];
  const RINGS = [0, 0.35, 0.7, 1.0, 1.3];
  const SEG = 14;
  const point = (s: number, d: number) => {
    const i = Math.max(0, Math.min(track.s.length - 1, Math.round(s)));
    const along = s - track.s[i];
    const x = track.x[i] + track.tx[i] * along + track.tz[i] * d;
    const z = track.z[i] + track.tz[i] * along - track.tx[i] * d;
    return { i, x, z };
  };
  for (const p of damage.potholes) {
    const base = pos.length / 3;
    for (let r = 0; r < RINGS.length; r++) {
      const n = r === 0 ? 1 : SEG;
      for (let k = 0; k < n; k++) {
        const a = (k / SEG) * Math.PI * 2 + p.s;
        const rr = RINGS[r] * p.r * (r === 0 ? 0 : 0.92 + 0.08 * Math.sin(a * 3 + p.d));
        const q = point(p.s + Math.sin(a) * rr, p.d + Math.cos(a) * rr);
        pos.push(q.x, terrain.roadProfile(q.i, p.d + Math.cos(a) * rr, q.x, q.z, false) + 0.012, q.z);
        uv.push(RINGS[r], p.water ? 1 : 0);
        // Bowl shading: inner rings lean toward the centre.
        const lean = r === 0 ? 0 : RINGS[r] <= 1 ? 0.55 * RINGS[r] : 0;
        const dx = -Math.cos(a) * lean;
        const dz = -Math.sin(a) * lean;
        const l = Math.hypot(dx, 1, dz);
        const i0 = q.i;
        // Lateral (d) maps to the track's right vector; along (s) to its tangent.
        nor.push((track.tz[i0] * dx + track.tx[i0] * dz) / l, 1 / l, (-track.tx[i0] * dx + track.tz[i0] * dz) / l);
      }
    }
    for (let k = 0; k < SEG; k++) idx.push(base, base + 1 + ((k + 1) % SEG), base + 1 + k);
    for (let r = 1; r < RINGS.length - 1; r++) {
      const a0 = base + 1 + (r - 1) * SEG;
      const b0 = a0 + SEG;
      for (let k = 0; k < SEG; k++) {
        const k1 = (k + 1) % SEG;
        idx.push(a0 + k, a0 + k1, b0 + k, a0 + k1, b0 + k1, b0 + k);
      }
    }
  }
  for (const w of damage.washouts) {
    const i = Math.max(0, Math.min(track.s.length - 1, Math.round(w.s)));
    const hw = track.width[i] / 2 + 0.6;
    const COLS = 9;
    const ROWS = 7;
    const base = pos.length / 3;
    for (let r = 0; r < ROWS; r++) {
      const u = (r / (ROWS - 1)) * 2 - 1; // across the gully
      for (let c = 0; c < COLS; c++) {
        const d = (c / (COLS - 1)) * 2 * hw - hw;
        const s = w.s + w.skew * d + u * w.half * 1.3;
        const q = point(s, d);
        pos.push(q.x, terrain.roadProfile(q.i, d, q.x, q.z, false) + 0.012, q.z);
        uv.push(Math.abs(u) * 1.3, 0);
        nor.push(0, 1, 0);
      }
    }
    for (let r = 0; r < ROWS - 1; r++) {
      for (let c = 0; c < COLS - 1; c++) {
        const a = base + r * COLS + c;
        idx.push(a, a + COLS, a + 1, a + 1, a + COLS, a + COLS + 1);
      }
    }
  }
  if (!pos.length) return [];
  return [{ positions: new Float32Array(pos), normals: new Float32Array(nor), uvs: new Float32Array(uv), indices: new Uint32Array(idx) }];
}

/** Water lying in the puddle potholes: a small disc in each, just below the road surface. */
function puddleMesh(track: Track, terrain: TerrainModel, damage: RoadDamage): MeshData | null {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const SEG = 14;
  for (const p of damage.potholes) {
    if (!p.water) continue;
    const i = Math.max(0, Math.min(track.s.length - 1, Math.round(p.s)));
    const along = p.s - track.s[i];
    const cx = track.x[i] + track.tx[i] * along + track.tz[i] * p.d;
    const cz = track.z[i] + track.tz[i] * along - track.tx[i] * p.d;
    // Water lies just below the road surface: the ribbon's 1 m rows bridge the bowl, so a level
    // deeper down would hide under them.
    const level = terrain.roadProfile(i, p.d, cx, cz, false) - 0.018;
    const rr = p.r * 0.82;
    const base = pos.length / 3;
    pos.push(cx, level, cz);
    uv.push(0.5, 0.5);
    for (let k = 0; k < SEG; k++) {
      const a = (k / SEG) * Math.PI * 2;
      const w = rr * (0.9 + 0.1 * Math.sin(a * 3 + p.s));
      pos.push(cx + Math.cos(a) * w, level, cz + Math.sin(a) * w);
      uv.push(0.5 + Math.cos(a) * 0.5, 0.5 + Math.sin(a) * 0.5);
    }
    for (let k = 0; k < SEG; k++) idx.push(base, base + 1 + ((k + 1) % SEG), base + 1 + k);
  }
  if (!pos.length) return null;
  const positions = new Float32Array(pos);
  const normals = new Float32Array(positions.length);
  for (let i = 1; i < normals.length; i += 3) normals[i] = 1;
  return { positions, normals, uvs: new Float32Array(uv), indices: new Uint32Array(idx) };
}

export { CORRIDOR };
